// F3b: agent-host 发的审批卡片 —— 显 prompt + 批准/拒绝按钮
//
// 关键安全约束:
//   1. 签名通过 onclick 触发, 永远不通过文本输入 (防 prompt injection 拿到签名)
//   2. 签的内容是 canonical(payload), 含 prompt_sha256 (不是 prompt 原文)
//      → agent verify 时重 hash prompt 必须一致, 防替换攻击
//   3. 私钥不进 React state, 也不发到 server, 用完即丢

import { useState } from "react";

import { ensureIdentityKey } from "../lib/identity-key";
import { promptSha256Hex, signApproval } from "../lib/approval";
import { useI18n } from "../lib/i18n";

export interface ApprovalCardProps {
  data: {
    invocation_id: string;
    caller_user_id: string;
    room_id: string;
    adapter_id: string;
    prompt: string;
    prompt_sha256: string;
    expires_at: number;
  };
  /** 当前登录用户 id；只有他能批准（"你 @ 你批"） */
  currentUserId: string | null;
  /** 发裸 ws 文本 */
  sendRaw: (text: string) => void;
}

type Stage = "pending" | "signing" | "sent" | "expired";

export function ApprovalCard({ data, currentUserId, sendRaw }: ApprovalCardProps) {
  const { t } = useI18n();
  const [stage, setStage] = useState<Stage>(
    Date.now() > data.expires_at ? "expired" : "pending",
  );
  const [error, setError] = useState<string | null>(null);
  const isMine = currentUserId !== null && data.caller_user_id === currentUserId;

  async function decide(decision: "approve" | "deny") {
    setStage("signing");
    setError(null);
    try {
      // 防御性二次验算: 后端给的 prompt_sha256 必须 == 我 sha256(prompt)
      const ours = await promptSha256Hex(data.prompt);
      if (ours !== data.prompt_sha256) {
        throw new Error("prompt hash mismatch (data tampered)");
      }
      const { signer, publicKey } = await ensureIdentityKey();
      const payload = {
        version: 1 as const,
        invocation_id: data.invocation_id,
        caller_user_id: data.caller_user_id,
        room_id: data.room_id,
        adapter_id: data.adapter_id,
        prompt_sha256: data.prompt_sha256,
        decision,
        ts: Date.now(),
      };
      const signed = signApproval(signer, publicKey, payload);
      sendRaw(JSON.stringify(signed));
      setStage("sent");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStage("pending");
    }
  }

  return (
    <div className="approval-card">
      <div className="approval-card__head">
        <span className="approval-card__icon">🔐</span>
        <span className="approval-card__title">
          <code>@{data.adapter_id}</code> {t("approval.title")}
        </span>
      </div>
      <pre className="approval-card__prompt">{data.prompt}</pre>
      <div className="approval-card__meta">
        {t("approval.meta.invocation")} <code>{data.invocation_id}</code>
        {" · "}
        <span title={`sha256: ${data.prompt_sha256}`}>
          {t("approval.meta.hash")} {data.prompt_sha256.slice(0, 10)}…
        </span>
      </div>
      {!isMine && (
        <div className="approval-card__warn">
          {t("approval.warn.notMine", { user: data.caller_user_id })}
        </div>
      )}
      {error && <div className="approval-card__warn">{t("approval.error", { msg: error })}</div>}
      {stage === "pending" && isMine && (
        <div className="approval-card__actions">
          <button className="btn btn--primary" onClick={() => decide("approve")}>
            {t("approval.btn.approve")}
          </button>
          <button
            className="btn btn--ghost approval-card__deny"
            onClick={() => decide("deny")}
          >
            {t("approval.btn.deny")}
          </button>
        </div>
      )}
      {stage === "signing" && <div className="approval-card__hint">{t("approval.signing")}</div>}
      {stage === "sent" && (
        <div className="approval-card__hint approval-card__hint--ok">
          {t("approval.sent")}
        </div>
      )}
      {stage === "expired" && (
        <div className="approval-card__hint approval-card__hint--bad">
          {t("approval.expired")}
        </div>
      )}
    </div>
  );
}
